<script lang="ts">
  import clsx from 'clsx'
  import { onDestroy, onMount } from 'svelte'
  import captionsState from '../stores/captions.svelte'
  import componentAttributesStore from '../stores/componentAttributes.svelte'
  import videoStore from '../stores/video.svelte'
  import { LIVESTREAM_STATUS } from '../const/livestream'
  interface PlayerContainerProps {
    children?: () => any
  }

  let { children }: PlayerContainerProps = $props()

  let videoWidth = $derived(videoStore.state.videoWidth)
  let videoHeight = $derived(videoStore.state.videoHeight)

  let containerRef: HTMLDivElement | null = $state(null)

  let resizeObserver: ResizeObserver

  onMount(() => {
    resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === containerRef) {
          videoStore.state.playerWidth = entry.contentRect.width
          videoStore.state.playerHeight = entry.contentRect.height
        }
      }
    })
    resizeObserver.observe(containerRef!)
  })

  onDestroy(() => {
    if (resizeObserver) {
      resizeObserver.unobserve(containerRef!)
      resizeObserver.disconnect()
    }
  })

  $effect(() => {
    videoStore.state.containerElement = containerRef
  })

  const shortcuts: Record<string, () => void> = {
    ' ': () => videoStore.state.videoController?.togglePlayPause(),
    c: () =>
      captionsState.setCaptionsEnabled(!captionsState.state.subtitleEnabled),
    s: () =>
      (videoStore.state.settingsMenuOpen = !videoStore.state.settingsMenuOpen),
    f: () => videoStore.state.videoController?.toggleFullscreen(),
    p: () => videoStore.state.videoController?.togglePip(),
    m: () => videoStore.state.videoController?.toggleMute(),
    ArrowRight: () => videoStore.state.videoController?.seekForward(),
    ArrowLeft: () => videoStore.state.videoController?.seekBackward(),
    ArrowUp: () => videoStore.state.videoController?.increaseVolume(0.1),
    ArrowDown: () => videoStore.state.videoController?.increaseVolume(-0.1),
    Escape: () => videoStore.forceUserInactive(),
  }

  function handleKeydown(event: KeyboardEvent) {
    if (componentAttributesStore.state.disableHotkeys) {
      return
    }
    const videoController = videoStore.state.videoController
    if (!videoController) return

    if (event.key === 'Tab') {
      videoStore.forceUserActive()
    } else if (shortcuts[event.key]) {
      event.preventDefault()
      shortcuts[event.key]()
    }
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<div
  class={clsx(
    'relative w-full mx-auto overflow-hidden font-ovhcloud',
    videoStore.state.isMobileLayout && 'mobile',
    videoStore.state.playerWidth >= 200 && 'pxxs',
    videoStore.state.playerWidth >= 300 && 'pxs',
    videoStore.state.playerWidth >= 400 && 'psm',
    videoStore.state.playerWidth >= 600 && 'pmd',
  )}
  role="main"
  aria-roledescription="video player"
  aria-label="Video player"
  onmousemove={() => videoStore.mouseMove()}
  onmouseenter={() => (videoStore.state.mouseOver = true)}
  onmouseleave={() => {
    videoStore.state.mouseOver = false
    videoStore.forceUserInactive()
  }}
  onkeydown={(event) => handleKeydown(event)}
  tabindex="0"
  class:hidden={(videoStore.state.mediaDescriptor === null ||
    (videoStore.state.mediaDescriptor?.mediaType !== 'live' && !videoWidth) ||
    (videoStore.state.mediaDescriptor?.mediaType === 'live' &&
      videoStore.state.liveStatus === LIVESTREAM_STATUS.LOADING)) &&
    !videoStore.state.errorMessage}
  style:width={componentAttributesStore.state.width}
  style:height={componentAttributesStore.state.height}
  style:aspect-ratio={videoWidth && videoHeight
    ? `${videoWidth} / ${videoHeight}`
    : ''}
  bind:this={containerRef}
>
  {@render children?.()}
</div>
